import { useState } from 'react';

/**
 * The post-run feedback form (playtest request).
 *
 * ── What it deliberately does NOT ask ─────────────────────────────────────────────────
 * No name, no email, no identity field of any kind — and none exists server-side to put
 * one in. It also never asks anything the finished run already knows: which towers were
 * fielded, what each dealt, the wave reached, the map, the mutator and the seed all ride
 * along automatically. A form that asks for what telemetry already has spends the
 * player's patience on nothing.
 *
 * So the four questions are only the things a machine cannot see: whether it FELT fair,
 * what they wanted to say, what they wish existed, and whether they mean to come back.
 */

export interface FeedbackAnswers {
  difficulty: number;
  returningTomorrow: boolean | null;
  thoughts: string;
  feature: string;
}

/**
 * -2 … +2 rather than labels, so the answer can be averaged and plotted against the wave
 * actually reached. "Died on wave 9, said too easy" and "won, said too hard" are both
 * loud, and neither is visible from either half alone.
 */
const DIFFICULTY: { value: number; label: string }[] = [
  { value: -2, label: 'Far too easy' },
  { value: -1, label: 'A bit easy' },
  { value: 0, label: 'About right' },
  { value: 1, label: 'A bit hard' },
  { value: 2, label: 'Far too hard' },
];

const TEXT_MAX = 500;

export function FeedbackForm({
  onSubmit,
  onDismiss,
}: {
  onSubmit: (answers: FeedbackAnswers) => void;
  onDismiss: () => void;
}) {
  const [difficulty, setDifficulty] = useState<number | null>(null);
  const [returning, setReturning] = useState<boolean | null>(null);
  const [thoughts, setThoughts] = useState('');
  const [feature, setFeature] = useState('');
  const [sent, setSent] = useState(false);

  if (sent) {
    return (
      <section className="feedback feedback--done">
        <p>Thanks — that goes straight into the balance notes.</p>
      </section>
    );
  }

  return (
    <section className="feedback">
      <header className="feedback__head">
        <b>Got twenty seconds?</b>
        <button type="button" className="feedback__skip" onClick={onDismiss}>
          Not now
        </button>
      </header>
      <p className="feedback__note muted">
        Anonymous — no name, no email. Your board and wave come along on their own.
      </p>

      <p className="feedback__q">How did that run feel?</p>
      <div className="feedback__scale">
        {DIFFICULTY.map((opt) => (
          <button
            key={opt.value}
            type="button"
            className={`feedback__chip${difficulty === opt.value ? ' feedback__chip--on' : ''}`}
            aria-pressed={difficulty === opt.value}
            onClick={() => setDifficulty(opt.value)}
          >
            {opt.label}
          </button>
        ))}
      </div>

      <p className="feedback__q">Anything on your mind?</p>
      <textarea
        className="feedback__text"
        rows={2}
        maxLength={TEXT_MAX}
        value={thoughts}
        placeholder="A tower that felt broken, something confusing, anything…"
        onChange={(e) => setThoughts(e.target.value)}
      />

      <p className="feedback__q">Anything you wish the game had?</p>
      <textarea
        className="feedback__text"
        rows={2}
        maxLength={TEXT_MAX}
        value={feature}
        placeholder="A feature request."
        onChange={(e) => setFeature(e.target.value)}
      />

      <p className="feedback__q">Coming back tomorrow?</p>
      <div className="feedback__scale">
        <button
          type="button"
          className={`feedback__chip${returning === true ? ' feedback__chip--on' : ''}`}
          aria-pressed={returning === true}
          onClick={() => setReturning(true)}
        >
          Yes
        </button>
        <button
          type="button"
          className={`feedback__chip${returning === false ? ' feedback__chip--on' : ''}`}
          aria-pressed={returning === false}
          onClick={() => setReturning(false)}
        >
          No
        </button>
      </div>

      <button
        type="button"
        className="primary feedback__send"
        // The scale is the one required answer — it is the question the whole form exists
        // for, and a row with only free text cannot be plotted against anything.
        disabled={difficulty === null}
        onClick={() => {
          if (difficulty === null) return;
          setSent(true);
          onSubmit({ difficulty, returningTomorrow: returning, thoughts, feature });
        }}
      >
        {difficulty === null ? 'Pick a difficulty first' : 'Send'}
      </button>
    </section>
  );
}
